import { BlockBlobClient } from '@azure/storage-blob';

export type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'error' | 'cancelled';

export interface FileUploadItem {
  id: string;
  sasUrl: string;
  file: File;
  size: number;
  status: FileUploadStatus;
  progress: number;
  error: Error | null;
  abortController: AbortController;
  subscribers: Set<(item: FileUploadItem) => void>;
}

interface UploadFileOptions {
  onUploadComplete?: (id: string) => void;
  /** Fired when the blob upload fails. Not fired when the upload was cancelled by the caller. */
  onUploadError?: (id: string, error: Error) => void;
  // onProgress?: (id: string, progress: number) => void;
}

class AzureFileUploadManager {
  private uploads = new Map<string, FileUploadItem>();
  private listSubscribers = new Set<(list: FileUploadItem[]) => void>();

  uploadFile(id: string, file: File, path: string, token: string, options: UploadFileOptions = {}) {
    if (this.uploads.has(id)) throw new Error(`Upload with id '${id}' already exists.`);

    const abortController = new AbortController();
    const size = file.size;
    const sasUrl = `${path}?${token}`;

    const uploadItem: FileUploadItem = {
      id,
      sasUrl,
      file,
      size,
      status: 'uploading',
      progress: 0,
      error: null,
      abortController,
      subscribers: new Set(),
    };

    this.uploads.set(id, uploadItem);
    this.notify(id);
    this.notifyList();

    const blobClient = new BlockBlobClient(sasUrl);

    const MULTIPART_SIZE = 1 * 1024 * 1024; // 1 MB
    const shouldUseMultipart = size > MULTIPART_SIZE;

    const blockSize = shouldUseMultipart ? MULTIPART_SIZE : size;
    const maxSingleShotSize = shouldUseMultipart ? MULTIPART_SIZE : size;

    blobClient
      .uploadData(file, {
        abortSignal: abortController.signal,
        blockSize: blockSize,
        maxSingleShotSize: maxSingleShotSize,
        onProgress: (ev) => {
          const current = this.uploads.get(id);
          if (!current) return;
          current.progress = Math.round((ev.loadedBytes / size) * 100);
          this.notify(id);
          this.notifyList();
        },
      })
      .then(() => {
        const current = this.uploads.get(id);
        if (!current) return;
        current.status = 'success';
        current.progress = 100;
        this.notify(id);
        this.notifyList();
        options.onUploadComplete?.(id);
      })
      .catch((error) => {
        const current = this.uploads.get(id);
        if (!current) return;
        const wasCancelled = abortController.signal.aborted;
        current.status = wasCancelled ? 'cancelled' : 'error';
        current.error = error;
        this.notify(id);
        this.notifyList();
        if (!wasCancelled) {
          options.onUploadError?.(id, error instanceof Error ? error : new Error(String(error)));
        }
      });
  }

  /**
   * Download file from Azure Blob Storage and force browser to use custom filename
   */
  downloadFile(documentName: string, downloadPath: string, downloadToken: string, target: string = '_self') {
    try {
      const url = `${downloadPath}?${downloadToken}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.target = target;
      downloadLink.download = documentName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }

  deleteFile(id: string) {
    const item = this.uploads.get(id);
    if (item) {
      const blobClient = new BlockBlobClient(item.sasUrl);
      blobClient.deleteIfExists();
      this.uploads.delete(id);
    }
  }

  getUpload(id: string): FileUploadItem | undefined {
    return this.uploads.get(id);
  }

  getAll(): FileUploadItem[] {
    return Array.from(this.uploads.values());
  }

  cancelUpload(id: string) {
    const item = this.uploads.get(id);
    if (item) {
      item.abortController.abort();
    }
  }

  deleteUpload(id: string) {
    const item = this.uploads.get(id);
    if (item) {
      this.uploads.delete(id);
      // Notify list subscribers so the removed entry doesn't stay as "uploading".
      this.notifyList();
    }
  }

  subscribe(id: string, callback: (item: FileUploadItem) => void) {
    const item = this.uploads.get(id);
    if (item) {
      item.subscribers.add(callback);
      callback(item);
    }
  }

  unsubscribe(id: string, callback: (item: FileUploadItem) => void) {
    const item = this.uploads.get(id);
    if (item) {
      item.subscribers.delete(callback);
    }
  }

  subscribeList(callback: (list: FileUploadItem[]) => void) {
    this.listSubscribers.add(callback);
    callback(this.getAll());
  }

  unsubscribeList(callback: (list: FileUploadItem[]) => void) {
    this.listSubscribers.delete(callback);
  }

  private notify(id: string) {
    const item = this.uploads.get(id);
    if (item) {
      item.subscribers.forEach((callback) => callback(item));
    }
  }

  private notifyList() {
    const list = this.getAll();
    this.listSubscribers.forEach((cb) => cb(list));
  }
}

export const azureFileUploadManager = new AzureFileUploadManager();
